import { lookup } from "@repo/vihangam-yog-shared";
import {
  ArrowLeftRight,
  Check,
  RotateCcw,
  Search,
  UserPlus,
  X,
} from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

import { splitKundsOf } from "../event-registrations/kunds";
import { isNotComing, isSeva } from "../event-registrations/notComing";
import {
  lastHawanLabel,
  partyMemberLabel,
  partyTags,
  type Registration,
} from "../event-registrations/shared";

import { KundLocator } from "./KundLocator";

export type GuestCardState =
  | {
      kind: "guest";
      registration: Registration;
      /** 1-based party position when the scan was one person's own pass. */
      person?: number | null;
    }
  | {
      kind: "wrong-event";
      name: string;
      confirmationRef: string;
      eventSlug: string;
      eventTitle: string;
      eventDateLabel: string;
      /** Their booking there is marked not coming (seva) and no one on it
       *  has arrived. */
      notComing: boolean;
    }
  /** `offline` when the lookup itself failed, so nobody is told a paid
   *  guest has no booking just because the venue wifi dropped. */
  | { kind: "not-found"; ref: string; offline: boolean }
  | { kind: "ambiguous"; registrations: Registration[] };

/** "6:42 pm": when someone came through the door. */
export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "1 person", "2 people", "3 people (2 adults, 1 child)". */
export function peopleSummary(
  r: Pick<Registration, "totalPeople" | "adults" | "children">,
): string {
  if (r.totalPeople === 1) {
    return "1 person";
  }
  const total = `${r.totalPeople} people`;
  if (r.children === 0) {
    return total;
  }
  const children = `${r.children} ${r.children === 1 ? "child" : "children"}`;
  if (r.adults === 0) {
    return `${total} (${children})`;
  }
  return `${total} (${r.adults} ${r.adults === 1 ? "adult" : "adults"}, ${children})`;
}

/** How a hand-added or discounted place was covered, as the guest list
 *  names it. A card payment has no method stored, only a receipt. */
const PAID_BY_LABEL = {
  bank_transfer: "Bank transfer",
  gifted: "Gifted",
  discount: "Discount code",
} satisfies Record<string, string>;

function paidByOf(r: Registration): string | null {
  if (r.paymentMethod) {
    return lookup(PAID_BY_LABEL, r.paymentMethod) ?? null;
  }
  return r.bookingType === "paid" && r.receiptUrl ? "Card" : null;
}

const SOFT_TEXT = "text-[color:var(--color-ink-soft)]";
const PRIMARY_BUTTON =
  "inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[color:var(--color-amber)] px-5 text-[18px] font-semibold text-[color:var(--color-bg)] transition-opacity hover:opacity-90 disabled:opacity-50";
const OUTLINE_BUTTON =
  "inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full border-2 border-[color:var(--color-line-strong)] px-5 text-[18px] font-semibold disabled:opacity-50";

/**
 * The bottom sheet every path ends in: a scan hit, a tapped row, a deep
 * link. Status, big name, who is in the booking, one big action pinned
 * to the bottom so a party of 12 never pushes it off the screen. The
 * other-event, not-found and which-guest states share the frame so the
 * volunteer's eye always lands in the same place.
 */
export function GuestCard({
  state,
  busy,
  error,
  justDone,
  onCheckIn,
  onUndo,
  onTogglePerson,
  onPick,
  onOpenEvent,
  onSearch,
  onAddWalkIn,
  onClose,
}: {
  state: GuestCardState;
  busy: boolean;
  error: string | null;
  /** Flash the success state (the caller clears it). */
  justDone: boolean;
  /** The big action: the whole registration, or one person when given. */
  onCheckIn: (r: Registration, person?: number) => void;
  onUndo: (r: Registration, person?: number) => void;
  /** A tapped party row; the card stays open. */
  onTogglePerson: (r: Registration, person: number, checkedIn: boolean) => void;
  onPick: (r: Registration) => void;
  onOpenEvent: (slug: string) => void;
  /** Not found: close the camera and the card, and go to the search. */
  onSearch: () => void;
  /** Not found: close the camera and the card, and open the walk-in form. */
  onAddWalkIn: () => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const actionRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const focusKey =
    state.kind === "guest" ? `guest-${state.registration.id}` : state.kind;

  // Land on the main action whenever the card opens on someone new, so a
  // keyboard or screen reader starts where the volunteer's eye does.
  useEffect(() => {
    (actionRef.current ?? closeRef.current)?.focus();
  }, [focusKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }
      // The camera underneath closes on Escape too: this card goes first,
      // and only this card.
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center md:items-center md:p-6">
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-[color:var(--color-shadow)]/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-[color:var(--color-line)] shadow-2xl transition-colors md:rounded-2xl ${
          justDone
            ? "bg-emerald-700 text-white"
            : "bg-[color:var(--color-paper)] text-[color:var(--color-ink)]"
        }`}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full opacity-80 hover:opacity-100"
        >
          <X size={22} aria-hidden="true" />
        </button>

        {state.kind === "guest" ? (
          <GuestBody
            r={state.registration}
            person={state.person ?? null}
            titleId={titleId}
            actionRef={actionRef}
            busy={busy}
            error={error}
            justDone={justDone}
            onCheckIn={onCheckIn}
            onUndo={onUndo}
            onTogglePerson={onTogglePerson}
          />
        ) : null}

        {state.kind === "wrong-event" ? (
          <Sheet
            footer={
              <button
                ref={actionRef}
                type="button"
                onClick={() => onOpenEvent(state.eventSlug)}
                className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[color:var(--color-ink)] px-5 text-[16px] font-semibold text-[color:var(--color-paper)]"
              >
                <ArrowLeftRight size={18} aria-hidden="true" /> Switch to that
                event
              </button>
            }
          >
            <div className="flex flex-wrap gap-2">
              <StatusPill tone="plain">
                This pass is for another event
              </StatusPill>
              {state.notComing ? (
                <StatusPill tone="plain">Not coming</StatusPill>
              ) : null}
            </div>
            <p
              id={titleId}
              className="mt-3 pr-12 text-[28px] leading-tight font-bold break-words"
            >
              {state.name}
            </p>
            <p className="mt-2 text-[16px] leading-relaxed">
              This booking is for <strong>{state.eventTitle}</strong>
              {state.eventDateLabel ? ` on ${state.eventDateLabel}` : ""}.
            </p>
            <ReferenceLine value={state.confirmationRef} tone={SOFT_TEXT} />
          </Sheet>
        ) : null}

        {state.kind === "not-found" ? (
          <Sheet
            footer={
              <div className="flex flex-col gap-2.5">
                <button
                  ref={actionRef}
                  type="button"
                  onClick={onSearch}
                  className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[color:var(--color-ink)] px-5 text-[16px] font-semibold text-[color:var(--color-paper)]"
                >
                  <Search size={18} aria-hidden="true" /> Search by name
                </button>
                {state.offline ? null : (
                  <button
                    type="button"
                    onClick={onAddWalkIn}
                    className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full border-2 border-[color:var(--color-line-strong)] px-5 text-[16px] font-semibold"
                  >
                    <UserPlus size={18} aria-hidden="true" /> Add as a walk-in
                  </button>
                )}
              </div>
            }
          >
            <p
              id={titleId}
              className="pr-12 text-[24px] leading-tight font-bold"
            >
              {state.offline
                ? "Couldn't check this pass"
                : "We couldn't find this booking"}
            </p>
            <ReferenceLine value={state.ref} tone={SOFT_TEXT} />
            <p className="mt-3 text-[16px] leading-relaxed">
              {state.offline
                ? "Check your signal, then scan the pass again."
                : "Search the list by their name or phone. If they haven't booked, add them as a walk-in."}
            </p>
          </Sheet>
        ) : null}

        {state.kind === "ambiguous" ? (
          <Sheet>
            <p
              id={titleId}
              className="pr-12 text-[24px] leading-tight font-bold"
            >
              Which guest is this?
            </p>
            <p className="mt-2 text-[16px] leading-relaxed">
              The camera couldn't read the code clearly. Tap the right name.
            </p>
            <div className="mt-4 space-y-2">
              {state.registrations.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onPick(r)}
                  className="flex min-h-14 w-full flex-col items-start justify-center rounded-xl border border-[color:var(--color-line-strong)] px-4 py-2 text-left"
                >
                  <span className="text-[16px] font-semibold break-words">
                    {r.name}
                  </span>
                  <span className={`text-[14px] ${SOFT_TEXT}`}>
                    {peopleSummary(r)} ·{" "}
                    <span className="font-mono">{r.confirmationRef}</span>
                  </span>
                </button>
              ))}
            </div>
          </Sheet>
        ) : null}
      </div>
    </div>
  );
}

/** The card's scrolling body over its fixed footer. */
function Sheet({
  children,
  footer,
}: {
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <>
      <div className="min-h-0 overflow-y-auto overscroll-contain px-6 pt-6 pb-4">
        {children}
      </div>
      <div className="px-6 pt-2 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        {footer}
      </div>
    </>
  );
}

type PillTone = "done" | "here" | "part" | "plain";

const PILL_TONE = {
  done: "border-white/60 text-white",
  here: "border-[color:var(--color-success)]/50 text-[color:var(--color-success)]",
  part: "border-[color:var(--color-amber)]/50 text-[color:var(--color-amber)]",
  plain:
    "border-[color:var(--color-line-strong)] text-[color:var(--color-ink)]",
} satisfies Record<PillTone, string>;

function StatusPill({
  tone,
  children,
}: {
  tone: PillTone;
  children: ReactNode;
}) {
  return (
    <p
      className={`inline-flex max-w-[calc(100%-3rem)] rounded-full border px-3 py-1 text-[15px] font-semibold ${PILL_TONE[tone]}`}
    >
      {children}
    </p>
  );
}

function ReferenceLine({ value, tone }: { value: string; tone: string }) {
  return (
    <p className={`mt-1 text-[14px] ${tone}`}>
      Reference <span className="font-mono">{value}</span>
    </p>
  );
}

function GuestBody({
  r,
  person,
  titleId,
  actionRef,
  busy,
  error,
  justDone,
  onCheckIn,
  onUndo,
  onTogglePerson,
}: {
  r: Registration;
  person: number | null;
  titleId: string;
  actionRef: RefObject<HTMLButtonElement | null>;
  busy: boolean;
  error: string | null;
  justDone: boolean;
  onCheckIn: (r: Registration, person?: number) => void;
  onUndo: (r: Registration, person?: number) => void;
  onTogglePerson: (r: Registration, person: number, checkedIn: boolean) => void;
}) {
  const arrived = Boolean(r.checkedInAt);
  // A per-person pass names someone on the list; a stale position (the
  // party was edited since the email) falls back to the whole party.
  const focus =
    person !== null && person >= 1 && person <= r.party.length
      ? { position: person, member: r.party[person - 1] }
      : null;
  const focusMember = focus?.member;
  const arrivedCount = r.party.filter((m) => m.arrivedAt).length;
  const lastHawan = lastHawanLabel(r.lastHawan);
  const partlyHere = !arrived && arrivedCount > 0;
  const shownName = focusMember ? focusMember.name : r.name;
  const firstName = shownName.trim().split(/\s+/)[0] ?? shownName;
  const soft = justDone ? "text-white" : SOFT_TEXT;
  const tierLabel = r.paymentBreakdown?.tierLabel ?? null;
  const paidBy = paidByOf(r);
  // A group of 6 or more on a Full Kund has two Kunds to split across.
  const splitKunds = splitKundsOf(r);
  const splitLine =
    splitKunds === null
      ? null
      : `${splitKunds} Kunds: this group can split across ${splitKunds === 2 ? "both" : "them"}`;
  const placeLine = [
    tierLabel ? `Type of place: ${tierLabel}` : null,
    paidBy ? `Paid by: ${paidBy}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const hereAt = focusMember ? (focusMember.arrivedAt ?? null) : r.checkedInAt;
  const isHere = focusMember ? Boolean(focusMember.arrivedAt) : arrived;
  const alreadyHere = `Already checked in${hereAt ? ` at ${clockTime(hereAt)}` : ""}`;
  let status = "Not checked in yet";
  let tone: PillTone = "plain";
  if (justDone) {
    status = "Checked in";
    tone = "done";
  } else if (isHere) {
    status = alreadyHere;
    tone = "here";
  } else if (partlyHere && !focusMember) {
    status = `${arrivedCount} of ${r.party.length} here`;
    tone = "part";
  } else if (isNotComing(r)) {
    // Marked not coming, and at the door anyway: Check in still works.
    status = isSeva(r) ? "Not coming (seva)" : "Not coming";
  }

  let action: ReactNode;
  if (justDone) {
    action = (
      <div
        role="status"
        className="flex min-h-14 items-center justify-center gap-2 rounded-full bg-white/20 text-[20px] font-semibold"
      >
        <Check size={22} aria-hidden="true" /> Welcome, {firstName}
      </div>
    );
  } else if (focus && focusMember) {
    action = focusMember.arrivedAt ? (
      <button
        ref={actionRef}
        type="button"
        onClick={() => onUndo(r, focus.position)}
        disabled={busy}
        className={OUTLINE_BUTTON}
      >
        <RotateCcw size={18} aria-hidden="true" />{" "}
        {busy ? "Saving…" : `Undo ${focusMember.name}`}
      </button>
    ) : (
      <button
        ref={actionRef}
        type="button"
        onClick={() => onCheckIn(r, focus.position)}
        disabled={busy}
        className={PRIMARY_BUTTON}
      >
        <Check size={22} aria-hidden="true" />{" "}
        {busy ? "Saving…" : `Check in ${focusMember.name}`}
      </button>
    );
  } else if (arrived) {
    action = (
      <button
        ref={actionRef}
        type="button"
        onClick={() => onUndo(r)}
        disabled={busy}
        className={OUTLINE_BUTTON}
      >
        <RotateCcw size={18} aria-hidden="true" />{" "}
        {busy
          ? "Saving…"
          : r.party.length > 0
            ? "Undo check-in for everyone"
            : "Undo check-in"}
      </button>
    );
  } else {
    action = (
      <button
        ref={actionRef}
        type="button"
        onClick={() => onCheckIn(r)}
        disabled={busy}
        className={PRIMARY_BUTTON}
      >
        <Check size={22} aria-hidden="true" />{" "}
        {busy
          ? "Saving…"
          : r.party.length > 0
            ? "Check in everyone"
            : "Check in"}
      </button>
    );
  }

  return (
    <Sheet
      footer={
        <>
          {error ? (
            <p
              role="alert"
              className="mb-3 rounded-lg border border-red-700/40 bg-red-700/[0.07] px-3 py-2 text-[15px] font-medium text-red-700"
            >
              {error}
            </p>
          ) : null}
          {action}
        </>
      }
    >
      <StatusPill tone={tone}>{status}</StatusPill>
      <p
        id={titleId}
        className="mt-3 pr-12 text-[28px] leading-tight font-bold break-words"
      >
        {shownName}
      </p>
      <p className={`mt-2 text-[16px] ${soft}`}>
        {focusMember && focusMember.name !== r.name
          ? `Booked by ${r.name} · ${peopleSummary(r)}`
          : peopleSummary(r)}
      </p>
      {placeLine ? (
        <p className={`mt-1 text-[16px] ${soft}`}>{placeLine}</p>
      ) : null}
      {splitLine ? (
        <p className={`mt-1 text-[16px] ${soft}`}>{splitLine}</p>
      ) : null}
      {r.groupName ? (
        <p className={`mt-1 text-[16px] ${soft}`}>Group: {r.groupName}</p>
      ) : null}
      {lastHawan ? (
        <p className={`mt-1 text-[16px] ${soft}`}>Last Hawan: {lastHawan}</p>
      ) : null}
      <ReferenceLine value={r.confirmationRef} tone={soft} />
      <KundLocator kundPlace={r.kundPlace} inverted={justDone} />
      {r.party.length > 0 ? (
        <>
          <p className={`mt-4 text-[14px] ${soft}`}>
            Tap a name to tick them in
          </p>
          <ul className="mt-2 space-y-2 text-[16px] leading-snug">
            {r.party.map((m, i) => {
              const position = i + 1;
              const here = Boolean(m.arrivedAt);
              const highlighted = focus?.position === position;
              return (
                <li key={`${m.name}-${i}`}>
                  <button
                    type="button"
                    onClick={() => onTogglePerson(r, position, !here)}
                    disabled={busy || justDone}
                    aria-pressed={here}
                    aria-label={`${here ? "Undo" : "Check in"} ${m.name}`}
                    className={`flex min-h-12 w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors disabled:opacity-60 ${
                      highlighted
                        ? "border-[color:var(--color-amber)] bg-[color:var(--color-amber)]/10"
                        : "border-current/20"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${
                        here
                          ? justDone
                            ? "border-white bg-white text-emerald-700"
                            : "border-emerald-700 bg-emerald-700 text-white"
                          : "border-current/40"
                      }`}
                    >
                      {here ? <Check size={16} /> : null}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="break-words">{partyMemberLabel(m)}</span>
                      {partyTags(m).map((tag) => (
                        <span
                          key={tag}
                          className="badge border border-current/40"
                        >
                          {tag}
                        </span>
                      ))}
                    </span>
                    {m.arrivedAt ? (
                      <span
                        className={`shrink-0 text-[14px] tabular-nums ${soft}`}
                      >
                        {clockTime(m.arrivedAt)}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
      {r.registrantNote ? (
        <p className="mt-4 text-[16px] leading-relaxed">
          <span className="font-semibold">Their note:</span> {r.registrantNote}
        </p>
      ) : null}
    </Sheet>
  );
}
